import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface ToolCallHtmlReportProps {
  html: string | null
  isLoading: boolean
  isError: boolean
  className?: string
}

/** Renders sanitized HTML report content with loading skeleton and error fallback. */
export const ToolCallHtmlReport: React.FC<ToolCallHtmlReportProps> = ({
  html,
  isLoading,
  isError,
  className,
}): ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current && html) {
      containerRef.current.innerHTML = html
    }
  }, [html])

  if (isLoading) {
    return (
      <div className={cn('tool-call-html-report-loading flex flex-col gap-3 p-4', className)}>
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    )
  }

  if (isError || !html) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'tool-call-html-report prose dark:prose-invert max-w-none overflow-x-hidden p-4 w-full min-w-0',
        className
      )}
    />
  )
}
